import { useQueryClient } from '@tanstack/react-query';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

export interface CollectionCaseListItemApi {
  caseId: string;
  caseNumber: string;
  customerId: number;
  companyName: string | null;
  customerName: string;
  customerAccountNumber: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  priority: string;
  status: string;
  strategyId: number | null;
  strategyName: string | null;
  amount: number;
  baseAmount?: number;
  displayOutstandingAmount?: number;
  grossAmount?: number;
  displayGrossAmount?: number;
  daysInCollection: number;
  nextAction: string | null;
  nextActionDate: string | null;
  riskLevel: string | null;
  assignedTo: number | null;
  assignedToName: string | null;
  contactId: number | null;
  notes: string | null;
  currencyCode: string | null;
  createdAt: string;
  updatedAt: string | null;
  activityCount: number;
  lastActionType: string | null;
  lastActionDescription: string | null;
  lastActionActorName: string | null;
  lastActionDate: string | null;
  currentStage: number;
  totalStages: number;
}

export interface CreateCollectionCaseRequest {
  customerId: number;
  priorityId?: number;
  strategyId?: number;
  amount: number;
  nextAction?: string;
  nextActionDate?: string;
  riskLevelId?: number;
  contactId?: number;
  notes?: string;
  transactionIds?: number[];
  assignedToUserId: number;
  currencyCode?: string;
}

export interface CollectionCaseStatusOption {
  code: string;
  name: string;
}

export interface CollectionCasePriorityOption {
  code: string;
  name: string;
  sortOrder: number;
}

const mockStatuses: CollectionCaseStatusOption[] = [
  { code: 'Active', name: 'Active' },
  { code: 'On Hold', name: 'On Hold' },
  { code: 'Closed', name: 'Closed' },
  { code: 'Escalated', name: 'Escalated' },
];

const mockPriorities: CollectionCasePriorityOption[] = [
  { code: 'High', name: 'High', sortOrder: 1 },
  { code: 'Medium', name: 'Medium', sortOrder: 2 },
  { code: 'Low', name: 'Low', sortOrder: 3 },
];

export function useCollectionCaseStatuses() {
  const { apiGet } = useApi();

  return useModeAwareQuery<CollectionCaseStatusOption[], Error>(
    ['collection-case-statuses'],
    async ({ signal }) => {
      return apiGet<CollectionCaseStatusOption[]>('/api/v1/collection-cases/statuses', undefined, signal);
    },
    mockStatuses,
    { staleTime: 60 * 1000, refetchOnWindowFocus: false },
  );
}

export function useCollectionCasePriorities() {
  const { apiGet } = useApi();

  return useModeAwareQuery<CollectionCasePriorityOption[], Error>(
    ['collection-case-priorities'],
    async ({ signal }) => {
      return apiGet<CollectionCasePriorityOption[]>('/api/v1/collection-cases/priorities', undefined, signal);
    },
    mockPriorities,
    { staleTime: 60 * 1000, refetchOnWindowFocus: false },
  );
}

export interface UpdateCollectionCaseRequest {
  caseId: number;
  priorityId?: number;
  statusId?: number;
  strategyId?: number;
  amount: number;
  nextAction?: string;
  nextActionDate?: string;
  riskLevelId?: number;
  contactId?: number;
  notes?: string;
}

const priorityCodeToId: Record<string, number> = {
  High: 1,
  Medium: 2,
  Low: 3,
};

const statusCodeToId: Record<string, number> = {
  Active: 1,
  'On Hold': 2,
  Closed: 3,
  Promised: 4,
  Escalated: 5,
};

const riskLevelCodeToId: Record<string, number> = {
  Critical: 1,
  High: 2,
  Medium: 3,
  Low: 4,
};

export function mapPriorityToId(priority: string): number {
  return priorityCodeToId[priority] ?? 2;
}

export function mapStatusToId(status: string): number {
  return statusCodeToId[status] ?? 1;
}

export function mapRiskLevelToId(riskLevel: string): number | undefined {
  return riskLevelCodeToId[riskLevel];
}

export function useUpdateCollectionCase() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CollectionCaseListItemApi, Error, UpdateCollectionCaseRequest>(
    async (request) => {
      const { caseId, ...body } = request;
      return apiPut<CollectionCaseListItemApi>(`/api/v1/collection-cases/${caseId}`, body);
    },
    (request) => {
      const mockResponse: CollectionCaseListItemApi = {
        caseId: String(request.caseId),
        caseNumber: `CC-MOCK`,
        customerId: 0,
        companyName: null,
        customerName: 'Mock Customer',
        customerAccountNumber: null,
        priority: Object.entries(priorityCodeToId).find(([, v]) => v === request.priorityId)?.[0] ?? 'Medium',
        status: Object.entries(statusCodeToId).find(([, v]) => v === request.statusId)?.[0] ?? 'Active',
        strategyId: request.strategyId ?? null,
        strategyName: null,
        amount: request.amount,
        daysInCollection: 0,
        nextAction: request.nextAction ?? null,
        nextActionDate: request.nextActionDate ?? null,
        riskLevel: null,
        assignedTo: null,
        assignedToName: null,
        contactId: request.contactId ?? null,
        notes: request.notes ?? null,
        currencyCode: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        activityCount: 0,
        customerEmail: null,
        customerPhone: null,
        lastActionType: null,
        lastActionDescription: null,
        lastActionActorName: null,
        lastActionDate: null,
        currentStage: 0,
        totalStages: 1,
      };
      return mockResponse;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['collection-cases'] });
        queryClient.invalidateQueries({ queryKey: ['collection-cases-list'] });
        queryClient.invalidateQueries({ queryKey: ['case-audit-logs'] });
      },
    },
  );
}

export interface AssignCollectionCaseRequest {
  caseId: number;
  assignedToUserId: number;
}

export function useAssignCollectionCase() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CollectionCaseListItemApi, Error, AssignCollectionCaseRequest>(
    async (request) => {
      return apiPost<CollectionCaseListItemApi>(`/api/v1/collection-cases/${request.caseId}/assign`, {
        assignedToUserId: request.assignedToUserId,
      });
    },
    (request) => {
      const mockResponse: CollectionCaseListItemApi = {
        caseId: String(request.caseId),
        caseNumber: 'CC-MOCK',
        customerId: 0,
        companyName: null,
        customerName: 'Mock Customer',
        customerAccountNumber: null,
        priority: 'Medium',
        status: 'Active',
        strategyId: null,
        strategyName: null,
        amount: 0,
        daysInCollection: 0,
        nextAction: null,
        nextActionDate: null,
        riskLevel: null,
        assignedTo: request.assignedToUserId,
        assignedToName: 'Assigned User',
        contactId: null,
        notes: null,
        currencyCode: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        activityCount: 0,
        customerEmail: null,
        customerPhone: null,
        lastActionType: null,
        lastActionDescription: null,
        lastActionActorName: null,
        lastActionDate: null,
        currentStage: 0,
        totalStages: 1,
      };
      return mockResponse;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['collection-cases'] });
        queryClient.invalidateQueries({ queryKey: ['collection-cases-list'] });
        queryClient.invalidateQueries({ queryKey: ['case-audit-logs'] });
      },
    },
  );
}

export interface BulkReassignCasesRequest {
  caseIds: number[];
  assignedToUserId: number;
  notes?: string;
}

export interface BulkUpdateCaseStatusRequest {
  caseIds: number[];
  status: string;
  notes?: string;
}

interface BulkCaseOperationResponse {
  updatedCount: number;
}

export function useBulkReassignCases() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<BulkCaseOperationResponse, Error, BulkReassignCasesRequest>(
    async (request) => {
      return apiPost<BulkCaseOperationResponse>('/api/v1/collection-cases/bulk-reassign', request);
    },
    (request) => {
      return { updatedCount: request.caseIds.length };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['collection-cases'] });
        queryClient.invalidateQueries({ queryKey: ['collection-cases-list'] });
        queryClient.invalidateQueries({ queryKey: ['case-audit-logs'] });
      },
    },
  );
}

export function useBulkUpdateCaseStatus() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<BulkCaseOperationResponse, Error, BulkUpdateCaseStatusRequest>(
    async (request) => {
      return apiPost<BulkCaseOperationResponse>('/api/v1/collection-cases/bulk-update-status', request);
    },
    (request) => {
      return { updatedCount: request.caseIds.length };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['collection-cases'] });
        queryClient.invalidateQueries({ queryKey: ['collection-cases-list'] });
        queryClient.invalidateQueries({ queryKey: ['case-audit-logs'] });
      },
    },
  );
}

export function useCreateCollectionCase() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CollectionCaseListItemApi, Error, CreateCollectionCaseRequest>(
    async (request) => {
      return apiPost<CollectionCaseListItemApi>('/api/v1/collection-cases', request);
    },
    (request) => {
      const mockResponse: CollectionCaseListItemApi = {
        caseId: String(Date.now()),
        caseNumber: `CC-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-MOCK`,
        customerId: request.customerId,
        companyName: null,
        customerName: 'Mock Customer',
        customerAccountNumber: null,
        priority: request.priorityId === 1 ? 'High' : request.priorityId === 3 ? 'Low' : 'Medium',
        status: 'Active',
        strategyId: request.strategyId ?? null,
        strategyName: null,
        amount: request.amount,
        daysInCollection: 0,
        nextAction: request.nextAction ?? null,
        nextActionDate: request.nextActionDate ?? null,
        riskLevel: null,
        assignedTo: request.assignedToUserId,
        assignedToName: 'Assigned User',
        contactId: request.contactId ?? null,
        notes: request.notes ?? null,
        createdAt: new Date().toISOString(),
        updatedAt: null,
        activityCount: 0,
        customerEmail: null,
        customerPhone: null,
        lastActionType: null,
        lastActionDescription: null,
        lastActionActorName: null,
        lastActionDate: null,
        currentStage: 0,
        totalStages: 1,
        currencyCode: request.currencyCode ?? null,
      };
      return mockResponse;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['collection-cases'] });
        queryClient.invalidateQueries({ queryKey: ['collection-cases-list'] });
        queryClient.invalidateQueries({ queryKey: ['case-audit-logs'] });
        queryClient.invalidateQueries({ queryKey: ['case-activities'] });
        // Refresh transaction grids so the hasCase flag updates
        queryClient.invalidateQueries({ queryKey: ['transactions-graphql-list'] });
        queryClient.invalidateQueries({ queryKey: ['transactions-graphql-count'] });
        queryClient.invalidateQueries({ queryKey: ['customer-transactions-graphql-list'] });
        queryClient.invalidateQueries({ queryKey: ['customer-transactions-graphql-count'] });
      },
    },
  );
}
